// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — auto-rotate as a button of its own, under Help in the top
 * inline-end corner (it was a switch in Tools → More until 2026-10-03).
 *
 *   [ Sign in ]
 *   [ ? Help  ]
 *   [ ⟳ ]  ← press to spin the globe; while it spins, a speed slider
 *            opens beside it, toward the middle of the screen:
 *   ( 0.25× ═══●════ 4× ) 1×  [ ⟳ ]
 *
 * The viewer's choice holds until they press the button again (owner,
 * 2026-10-04): a drag only pauses the spin, and if anything else stops it
 * (a new primary globe after a layout change, a Khipu that ended) the
 * button starts it again on its next check. While a Khipu plays or this
 * device follows a room's lead, the button waits (`suspended`) and the
 * choice is kept. Only the primary globe spins: the camera sync turns the
 * others with it.
 *
 * The speed is remembered per browser (a convenience; it only sets how
 * fast, never whether).
 */

import { t } from '../i18n'
import { formatNumber } from '../i18n/format'

/** What the control drives: the primary globe. */
export interface AutoRotateTarget {
  isAutoRotating(): boolean
  /** 0 stops; 1 is the default speed (30° per 10 s). */
  setRotationRate(rate: number): void
}

export interface AutoRotateDeps {
  primary(): AutoRotateTarget | null
  announce?(message: string): void
  /** Analytics hook, as the Tools switch had: on or off, not the speed. */
  onChange?(on: boolean): void
  /** True while something else drives the camera (a playing Khipu, a room's lead): the choice waits. */
  suspended?(): boolean
  /** AYNI (2026-10-19): true where there is nothing to spin (the flat map): the button is greyed and the choice waits. */
  unavailable?(): boolean
}

/** Slider bounds, as multiples of the default speed. */
export const MIN_RATE = 0.25
export const MAX_RATE = 4
const STEP = 0.25
const SPEED_KEY = 'ayni-autorotate-speed'

function storedRate(): number {
  try {
    const v = Number(localStorage.getItem(SPEED_KEY))
    return Number.isFinite(v) && v >= MIN_RATE && v <= MAX_RATE ? v : 1
  } catch {
    return 1
  }
}

function saveRate(rate: number): void {
  try {
    localStorage.setItem(SPEED_KEY, String(rate))
  } catch {
    // storage refused: the speed lasts the visit
  }
}

const speedText = (rate: number): string =>
  t('autorotate.speed.value', { rate: formatNumber(rate, { maximumFractionDigits: 2 }) })

let syncTimer: number | null = null

/** Wire `#autorotate-control`. Safe to call twice. */
export interface AutoRotateHandle {
  sync(): void
  /** AYNI: spin or stop as a Khipu step says, at its speed; the button and slider show it. */
  set(on: boolean, rate?: number): void
  /** The speed the slider is at (a multiple of the default). */
  rate(): number
}

export function initAutoRotateControl(deps: AutoRotateDeps): AutoRotateHandle {
  const root = document.getElementById('autorotate-control')
  const button = document.getElementById('autorotate-btn') as HTMLButtonElement | null
  const slider = document.getElementById('autorotate-speed') as HTMLInputElement | null
  const readout = document.getElementById('autorotate-speed-value')
  if (!root || !button || !slider || !readout) return { sync: () => {}, set: () => {}, rate: () => 1 }

  let rate = storedRate()
  slider.min = String(MIN_RATE)
  slider.max = String(MAX_RATE)
  slider.step = String(STEP)
  slider.value = String(rate)
  readout.textContent = speedText(rate)
  slider.setAttribute('aria-valuetext', speedText(rate))

  const show = (on: boolean): void => {
    button.classList.toggle('active', on)
    button.setAttribute('aria-pressed', String(on))
    root.classList.toggle('open', on)
    const label = t(on ? 'autorotate.stop' : 'autorotate.start')
    button.title = label
    button.setAttribute('aria-label', label)
  }

  /** The viewer's choice: on until they switch it off. */
  let wanted = deps.primary()?.isAutoRotating() ?? false
  const sync = (): void => {
    show(wanted)
    const unavailable = deps.unavailable?.() ?? false
    button.disabled = unavailable
    if (unavailable) root.classList.remove('open')
    if (!wanted || unavailable || deps.suspended?.()) return
    const primary = deps.primary()
    if (primary && !primary.isAutoRotating()) primary.setRotationRate(rate)
  }

  if (root.dataset.wired !== 'true') {
    root.dataset.wired = 'true'
    button.addEventListener('click', () => {
      const primary = deps.primary()
      if (!primary || deps.unavailable?.()) return
      const next = !wanted
      wanted = next
      primary.setRotationRate(next ? rate : 0)
      show(next)
      deps.onChange?.(next)
      deps.announce?.(t(next ? 'tools.announce.autoRotate.on' : 'tools.announce.autoRotate.off'))
    })
    slider.addEventListener('input', () => {
      rate = Number(slider.value)
      readout.textContent = speedText(rate)
      slider.setAttribute('aria-valuetext', speedText(rate))
      saveRate(rate)
      const primary = deps.primary()
      // Only while spinning: the slider is shown only then, and moving it
      // must never start a globe the viewer stopped.
      if (wanted && primary) primary.setRotationRate(rate)
    })
  }

  const set = (on: boolean, next?: number): void => {
    if (typeof next === 'number' && Number.isFinite(next)) {
      rate = Math.min(MAX_RATE, Math.max(MIN_RATE, next))
      slider.value = String(rate)
      readout.textContent = speedText(rate)
      slider.setAttribute('aria-valuetext', speedText(rate))
    }
    wanted = on
    deps.primary()?.setRotationRate(on ? rate : 0)
    show(on)
  }

  if (syncTimer !== null) window.clearInterval(syncTimer)
  syncTimer = window.setInterval(sync, 500)
  sync()
  return { sync, set, rate: () => rate }
}
